/**
 * AlertDetailsTable — CRUD-style table for the Alert Details Report.
 *
 * Follows AreasPage.jsx patterns: COLUMN_DEFS, sort, filter, kebab menu,
 * and active filter bar. Column visibility toggle is managed by the parent.
 *
 * Props:
 *   data               — paginated rows for current page
 *   allData            — full UNFILTERED dataset (source for filter option values;
 *                        must not have column filters applied, so options stay stable)
 *   visibleColumns     — string[] of visible column keys
 *   sortColumn         — current sort column key
 *   sortOrder          — 'asc' | 'desc'
 *   onSort             — (columnKey) => void
 *   filters            — { columnKey: [value1, ...] }
 *   onFilterChange     — (columnKey, selectedValues) => void
 *   onClearFilters     — () => void
 *   onAddNote          — (event) => void
 *   onEditReason       — (event) => void
 *   eventNotesActive   — boolean
 *   isReasonEditable   — boolean
 *   tzOffset           — timezone offset for datetime display
 */

import { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { convertIsoToLocal, convertIsoToLocalDate } from '../../../../libs.jsx';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import ActiveFilterBar from '../../../../components/v2/ActiveFilterBar.jsx';
import NotesCell from '../../../../components/v2/NotesCell.jsx';
import CallsCell from '../../../../components/v2/CallsCell.jsx';
import EventRowActions from '../../../../components/v2/EventRowActions.jsx';
import ReasonCell from '../../../../components/v2/ReasonCell.jsx';
import EventDeviceCell from '../../../../components/v2/EventDeviceCell.jsx';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';
import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';
import { GRANDSTREAM_ONLY_COLUMNS, TIME_TO_CONNECT_INFO, timeToConnectCell, timeToConnectFilterValue } from '../timeToConnect.js';

// ---------------------------------------------------------------------------
// Helpers for date-based filter transforms
// ---------------------------------------------------------------------------

/** Extract just the hour label (e.g. "3 PM", "12 AM") from a local datetime string like "MM/dd/yyyy h:mmAM" */
function extractHourLabel(isoDatetime) {
    const local = convertIsoToLocal(isoDatetime);
    if (!local) return '';
    // local format: "MM/dd/yyyy h:mmAM" — extract hour + AM/PM
    const match = local.match(/(\d{1,2}):\d{2}(AM|PM)/i);
    if (!match) return '';
    return `${match[1]} ${match[2].toUpperCase()}`;
}

/** Extract just the date (e.g. "04/10/2026") from a local datetime string */
function extractDateLabel(isoDatetime) {
    return convertIsoToLocalDate(isoDatetime) || '';
}

/** Sort hour labels chronologically (12 AM, 1 AM, ... 11 AM, 12 PM, 1 PM, ... 11 PM) */
function sortHourLabels(a, b) {
    const toIdx = (label) => {
        const m = label.match(/^(\d{1,2})\s*(AM|PM)$/i);
        if (!m) return 99;
        let h = parseInt(m[1], 10);
        const isPM = m[2].toUpperCase() === 'PM';
        if (h === 12) h = 0;
        return isPM ? h + 12 : h;
    };
    return toIdx(a) - toIdx(b);
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

export const COLUMN_DEFS = [
    { key: 'notes', label: 'Notes', sortable: false, filterable: false },
    // Calls column is only surfaced for Grandstream campuses (gated by isGrandstream).
    { key: 'calls', label: 'Calls', sortable: false, filterable: false },
    { key: 'event_datetime_local', label: 'Event Start', sortable: true, filterable: true, required: true },
    { key: 'event_type_name', label: 'Category/Type', sortable: true, filterable: true },
    { key: 'resident_name', label: 'Resident', sortable: true, filterable: true },
    { key: 'area_name', label: 'Area', sortable: true, filterable: true },
    // Calculated location: where the system worked out the alert came from, as
    // opposed to the area the device is assigned to. Sortable/filterable to match
    // Area, which it sits beside.
    { key: 'estimated_location_name', label: 'Located Area', sortable: true, filterable: true },
    { key: 'description', label: 'Device / Serial', sortable: true, filterable: false },
    { key: 'display_name', label: 'Accepted By', sortable: true, filterable: true },
    { key: 'ack_datetime_local', label: 'Cleared', sortable: true, filterable: false },
    { key: 'finish_datetime_local', label: 'Finished', sortable: true, filterable: false },
    { key: 'response_time', label: 'Response (min)', sortable: true, filterable: true },
    // Grandstream campuses only, like Calls (GRANDSTREAM_ONLY_COLUMNS).
    { key: 'time_to_connect', label: 'Time to Connect (min)', sortable: true, filterable: true,
      info: { title: 'Time to Connect', body: TIME_TO_CONNECT_INFO } },
    { key: 'service_time', label: 'Service Time (min)', sortable: true, filterable: true },
    { key: 'reason_code_description', label: 'Reason', sortable: true, filterable: true }
];

// Build column labels map for ActiveFilterBar
const COLUMN_LABELS = {};
COLUMN_DEFS.forEach((col) => { COLUMN_LABELS[col.key] = col.label; });

// ---------------------------------------------------------------------------
// Cell renderers
// ---------------------------------------------------------------------------

function renderCell(row, colKey) {
    switch (colKey) {
        case 'event_datetime_local':
            return (
                <Link to={`/profiles/event/${row.event_id}`} className="dp-link">
                    {row.event_datetime_local ? convertIsoToLocal(row.event_datetime_local) : '\u2014'}
                </Link>
            );

        case 'event_type_name':
            return (
                <div>
                    <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-medium)' }}>
                        {row.event_category_name || ''}
                    </span>
                    <br />
                    <span className="dp-cell-text dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                        {row.event_type_name || ''}
                    </span>
                </div>
            );

        case 'resident_name':
            return row.resident_id ? (
                <Link to={`/profiles/resident/${row.resident_id}`} className="dp-link">
                    {row.resident_name || '\u2014'}
                </Link>
            ) : (
                <span className="dp-cell-text">{row.resident_name || '\u2014'}</span>
            );

        case 'area_name':
            return row.area_id ? (
                <Link to={`/profiles/area/${row.area_id}`} className="dp-link">
                    {row.area_name || '\u2014'}
                </Link>
            ) : (
                <span className="dp-cell-text">{row.area_name || '\u2014'}</span>
            );

        case 'estimated_location_name': {
            /* Links to the area profile like the Area column. The id is explicitly
               0 (not null) when the system could not work out a location, so test
               the number rather than truthiness — and a string "0" would pass a
               bare truthy check. */
            const estId = Number(row.estimated_location_id);
            const estLinkable = Number.isFinite(estId) && estId > 0;
            return estLinkable ? (
                <Link to={`/profiles/area/${estId}`} className="dp-link">
                    {row.estimated_location_name || '\u2014'}
                </Link>
            ) : (
                <span className="dp-cell-text">{row.estimated_location_name || '\u2014'}</span>
            );
        }

        case 'description':
            // Stacked: description + manufacture_uuid, linked by the alert rule.
            return <EventDeviceCell row={row} />;

        case 'display_name':
            return (
                <div>
                    <span className="dp-cell-text">{row.display_name || '\u2014'}</span>
                    <br />
                    <span className="dp-cell-text dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                        {row.accept_datetime_local ? convertIsoToLocal(row.accept_datetime_local) : ''}
                    </span>
                </div>
            );

        case 'ack_datetime_local':
            return (
                <span className="dp-cell-text">
                    {row.ack_datetime_local ? convertIsoToLocal(row.ack_datetime_local) : '\u2014'}
                </span>
            );

        case 'finish_datetime_local':
            return (
                <span className="dp-cell-text">
                    {row.finish_datetime_local ? convertIsoToLocal(row.finish_datetime_local) : '\u2014'}
                </span>
            );

        case 'response_time':
            return (
                <span className="dp-cell-text">
                    {row.response_time != null ? row.response_time : '\u2014'}
                </span>
            );

        case 'time_to_connect': {
            /* Minutes to the first answered intercom call; else "No answer" (a call was
               placed) or N/A (none was). Never 0 and never the response time. */
            const cell = timeToConnectCell(row);
            return (
                <span className={`dp-cell-text${cell.muted ? ' dp-cell-text--muted' : ''}`} title={cell.title}>
                    {cell.text}
                </span>
            );
        }

        case 'service_time': {
            /* NULL, not 0, is now "not measured" -- sp_getEventReport(Cloud)
               returns NULL for auto-closed, never-finished and (non-cloud)
               reason-0 events. The old `=== 0` test also swallowed a GENUINE
               0.0, i.e. an alert acked and finished inside the same rounded
               minute, which is a real measurement and should read as one.
               See docs/REPORT_PROC_AUDIT_PLAN.md E2. */
            const val = row.service_time;
            return (
                <span className="dp-cell-text">
                    {val == null || val === '' ? '-' : val}
                </span>
            );
        }

        case 'reason_code_description':
            return (
                <ReasonCell
                    reasonId={row.reason_id}
                    description={row.reason_code_description}
                    edited={row.reason_edits && row.reason_edits.length > 0}
                />
            );

        default:
            return <span className="dp-cell-text">{row[colKey] != null ? String(row[colKey]) : '\u2014'}</span>;
    }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AlertDetailsTable = ({
    data,
    allData,
    visibleColumns,
    sortColumn,
    sortOrder,
    onSort,
    filters,
    onFilterChange,
    onClearFilters,
    onAddNote,
    onEditReason,
    eventNotesActive,
    isReasonEditable,
    tzOffset,
    dateFilterMode,  // 'hour' | 'date' | undefined (default = raw datetime strings)
    isGrandstream = false,
}) => {
    // Always show kebab — View Details is always available
    const showKebab = true;

    // Build unique values for filterable columns from allData
    const filterValues = useMemo(() => {
        const vals = {};
        COLUMN_DEFS.filter((c) => c.filterable).forEach((col) => {
            if (col.key === 'event_datetime_local' && dateFilterMode) {
                // Transform to hour or date labels (fields are already local — no offset needed)
                const extractor = dateFilterMode === 'hour' ? extractHourLabel : extractDateLabel;
                const labels = new Set((allData || []).map((row) => extractor(row.event_datetime_local)));
                labels.delete('');
                const sorted = [...labels];
                if (dateFilterMode === 'hour') {
                    sorted.sort(sortHourLabels);
                } else {
                    sorted.sort();
                }
                vals[col.key] = sorted;
            } else {
                const unique = [...new Set((allData || []).map((row) => {
                    // Time to Connect filters on what the cell shows, so "No answer" and N/A
                    // are separate options instead of one blank.
                    if (col.key === 'time_to_connect') return timeToConnectFilterValue(row);
                    const v = row[col.key];
                    return v != null ? String(v) : '';
                }))].sort();
                vals[col.key] = unique;
            }
        });
        return vals;
    }, [allData, dateFilterMode]);

    // Column labels for ActiveFilterBar
    const handleRemoveFilter = useCallback((columnKey, value) => {
        const current = filters[columnKey] || [];
        const updated = current.filter((v) => v !== value);
        onFilterChange(columnKey, updated);
    }, [filters, onFilterChange]);

    // Calls and Time to Connect only exist for Grandstream campuses. Gating here (in
    // addition to the page's column config) keeps them hidden even if a stale key
    // lingers in visibleColumns after switching to a non-Grandstream campus.
    const visibleColDefs = COLUMN_DEFS.filter(
        (col) => visibleColumns.includes(col.key) && (isGrandstream || !GRANDSTREAM_ONLY_COLUMNS.includes(col.key))
    );

    return (
        <div className="ap-table-container">
            {/* Active filter bar */}
            <ActiveFilterBar
                filters={filters}
                columnLabels={COLUMN_LABELS}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={onClearFilters}
            />

            {/* Table */}
            <div className="dp-table-wrap">
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            {showKebab ? (
                                <th scope="col" className="ap-th ap-th--actions">Edit</th>
                            ) : null}
                            {visibleColDefs.map((col) => (
                                <th key={col.key} scope="col" className="ap-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                                    <div className="ap-th__content">
                                        {col.sortable ? (
                                            <button
                                                type="button"
                                                onClick={() => onSort(col.key)}
                                                className="ap-th__sort-btn"
                                                aria-label={`Sort by ${col.label}`}
                                            >
                                                {col.label}
                                                {sortColumn === col.key ? (
                                                    <svg className="ap-icon-sm" style={{ marginLeft: '2px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                                        {sortOrder === 'asc'
                                                            ? <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                                            : <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                                        }
                                                    </svg>
                                                ) : null}
                                            </button>
                                        ) : (
                                            <span>{col.label}</span>
                                        )}
                                        {col.info ? <HeaderInfoTip title={col.info.title} body={col.info.body} /> : null}

                                        {col.filterable ? (
                                            <FilterPopover
                                                columnKey={col.key}
                                                label={col.label}
                                                values={filterValues[col.key] || []}
                                                selectedValues={filters[col.key] || []}
                                                onChange={onFilterChange}
                                            />
                                        ) : null}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr className="dp-tr">
                                <td className="dp-td dp-td--center" colSpan={visibleColDefs.length + (showKebab ? 1 : 0)} style={{ padding: 'var(--space-8)' }}>
                                    <span className="dp-cell-text dp-cell-text--muted">No events found.</span>
                                </td>
                            </tr>
                        ) : null}
                        {data.map((row, idx) => (
                            <tr key={row.event_id || idx} className="dp-tr">
                                {showKebab ? (
                                    <td className="dp-td dp-td--center">
                                        <EventRowActions
                                            row={row}
                                            onEditReason={onEditReason}
                                            isReasonEditable={isReasonEditable}
                                        />
                                    </td>
                                ) : null}
                                {visibleColDefs.map((col) => (
                                    <td key={col.key} className="dp-td">
                                        {col.key === 'notes' ? (
                                            <NotesCell
                                                row={row}
                                                notes={row.notes}
                                                onClick={onAddNote}
                                                eventNotesActive={eventNotesActive}
                                                tzOffset={tzOffset}
                                                ariaPrefix={`Event ${row.event_id || ''}`}
                                            />
                                        ) : col.key === 'calls' ? (
                                            <CallsCell
                                                calls={row.calls}
                                                tzOffset={tzOffset}
                                                ariaPrefix={`Event ${row.event_id || ''}`}
                                            />
                                        ) : renderCell(row, col.key)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AlertDetailsTable;
